import { Transmit } from "@adonisjs/transmit-client";

export default defineNuxtPlugin(() => {
  const config = useRuntimeConfig();

  if (import.meta.client) {
    // PDF render pages are loaded headlessly by the CF worker — there is no
    // user session, so Transmit's SSE would 401 and reconnect in a loop
    // forever (and keep a connection open that delays networkidle). Nothing on
    // /pdf/** uses realtime, so skip constructing Transmit entirely and
    // provide null.
    const route = useRoute();
    if (route.path.startsWith("/pdf")) {
      return {
        provide: {
          transmit: null,
        },
      };
    }

    // `new Transmit()` opens the SSE connection in its constructor. On public
    // pages (marketing homepage) there's no session, so connecting eagerly
    // produces a 401 + reconnect loop. Construct lazily instead: the first
    // property access (e.g. `.subscription()`) creates the client. Nothing on
    // the public site ever touches $transmit, so no connection is opened.
    let transmit: Transmit | null = null;
    const getTransmit = () => {
      if (!transmit) {
        transmit = new Transmit({
          baseUrl: config.public.api,
          // The runtime option is `maxReconnectAttempts` (not `maxReconnectionAttempts`).
          maxReconnectAttempts: 5,
          onReconnectAttempt: (attempt) => {
            console.warn(`[Transmit] SSE reconnect attempt #${attempt}`);
          },
          onReconnectFailed: () => {
            console.warn("[Transmit] SSE reconnection failed after max attempts");
          },
        });
      }
      return transmit;
    };

    // Proxy the lazily-created instance. Methods are bound to the real object
    // so `this` (private-field lookups) resolves correctly.
    const lazyTransmit = new Proxy({} as Transmit, {
      get(_target, prop) {
        const real = getTransmit();
        const value = (real as Record<PropertyKey, unknown>)[prop];
        return typeof value === "function"
          ? (value as (...args: unknown[]) => unknown).bind(real)
          : value;
      },
    });

    return {
      provide: {
        // markRaw prevents Vue from making this reactive, which would break private fields
        transmit: markRaw(lazyTransmit),
      },
    };
  }

  // Server-side: return null
  return {
    provide: {
      transmit: null,
    },
  };
});
